import { Loader2, Minus, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import type { ProductSearchItem } from "../../types/productV3";
import type { ProductWeight } from "../../services/advisorGroupService";
import { formatWeight, roundToDecimals } from "../../utils/numberUtils";
import { BASE_STRATEGY_ALLOCATION_LIMIT } from "../../utils/strategyAllocationLimits";
import {
  computeDraftDiff,
  type DraftDiffItem,
  type StrategyDraftEntry,
  ProductLookupKey,
  ProductLookupEntry,
} from "../../utils/strategyUtils";

const PIE_COLORS = [
  "#14b8a6", "#f59e0b", "#a855f7", "#f43f5e", "#10b981",
  "#3b82f6", "#ec4899", "#8b5cf6", "#06b6d4", "#84cc16",
  "#ef4444", "#6366f1", "#d946ef", "#0ea5e9", "#eab308",
  "#f97316", "#22c55e", "#e879f9", "#2dd4bf", "#fb923c",
];

interface EditEntry {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  weight: number;
  displayName: string;
}

export interface GroupStrategyEditorProps {
  strategy: ProductWeight[] | null;
  productItems: ProductSearchItem[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  memberCount: number;
  onSave: (strategies: ProductWeight[]) => Promise<void>;
  onCancel: () => void;
}

function resolveDisplayName(
  entityType: "PORTFOLIO" | "BASKET",
  productId: number,
  lookup: Map<ProductLookupKey, ProductLookupEntry>
): string {
  const key: ProductLookupKey = `${entityType}-${productId}`;
  const entry = lookup.get(key);
  if (entry?.title) return entry.title;
  return `${entityType} #${productId}`;
}

function toSavedDraftEntries(
  strategy: ProductWeight[] | null,
  lookup: Map<ProductLookupKey, ProductLookupEntry>
): StrategyDraftEntry[] {
  return (strategy ?? []).map((pw) => ({
    entityType: pw.entityType,
    productId: pw.productId,
    weight: pw.weight,
    displayName: resolveDisplayName(pw.entityType, pw.productId, lookup),
  }));
}

function toDraftEntries(entries: EditEntry[]): StrategyDraftEntry[] {
  return entries
    .filter((e) => e.weight > 0)
    .map((e) => ({
      entityType: e.entityType,
      productId: e.productId,
      weight: e.weight,
      displayName: e.displayName,
    }));
}

// -- Donut chart (ported from DraftDonutCard) --

function AllocationDonut({ entries, totalWeight }: { entries: EditEntry[]; totalWeight: number }) {
  const activeEntries = entries.filter((e) => e.weight > 0);
  const size = 180;
  const strokeWidth = 28;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const effectiveTotal = Math.max(totalWeight, 100);
  let cumulative = 0;
  const slices = activeEntries.map((entry, idx) => {
    const fraction = effectiveTotal > 0 ? entry.weight / effectiveTotal : 0;
    const offset = cumulative;
    cumulative += fraction;
    return { entry, fraction, offset, color: PIE_COLORS[idx % PIE_COLORS.length] };
  });
  const cashWeight = roundToDecimals(Math.max(0, 100 - totalWeight));

  return (
    <div className="bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Allocation Preview
        </span>
        {cashWeight > 0 && (
          <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
            {formatWeight(cashWeight)}% Cash
          </span>
        )}
      </div>
      <div className="flex flex-col items-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mb-3">
          <circle
            cx={center} cy={center} r={radius} fill="none"
            stroke="currentColor" className="text-gray-200 dark:text-gray-800" strokeWidth={strokeWidth}
          />
          {slices.map((slice) => (
            <circle
              key={`${slice.entry.entityType}-${slice.entry.productId}`}
              cx={center} cy={center} r={radius} fill="none"
              stroke={slice.color} strokeWidth={strokeWidth}
              strokeDasharray={`${slice.fraction * circumference} ${circumference}`}
              strokeDashoffset={-slice.offset * circumference}
              transform={`rotate(-90 ${center} ${center})`}
              className="transition-all duration-500"
            />
          ))}
          <text
            x={center} y={center - 6} textAnchor="middle"
            className="fill-gray-900 dark:fill-white"
            style={{ fontSize: "24px", fontWeight: 700 }}
          >
            {formatWeight(totalWeight)}%
          </text>
          <text
            x={center} y={center + 12} textAnchor="middle"
            className="fill-gray-500 dark:fill-gray-400"
            style={{ fontSize: "11px" }}
          >
            allocated
          </text>
        </svg>
        <div className="w-full space-y-1 max-h-[160px] overflow-y-auto">
          {slices.map((slice) => (
            <div
              key={`${slice.entry.entityType}-${slice.entry.productId}`}
              className="flex items-center justify-between gap-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: slice.color }} />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{slice.entry.displayName}</span>
              </div>
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400 flex-shrink-0">
                {formatWeight(slice.entry.weight)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -- Save confirmation modal (ported from StrategySaveConfirmation) --

type ChangeType = "Added" | "Removed" | "Modified" | "Unchanged";

interface ChangeRow {
  type: ChangeType;
  displayName: string;
  entityType: "PORTFOLIO" | "BASKET";
  currentWeight: number | null;
  newWeight: number | null;
}

const TYPE_COLORS: Record<ChangeType, string> = {
  Added: "text-green-500",
  Removed: "text-red-400",
  Modified: "text-rp-amber",
  Unchanged: "text-gray-500",
};

function GroupStrategySaveConfirmation({
  isOpen,
  onClose,
  onConfirm,
  entries,
  savedEntries,
  memberCount,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  entries: StrategyDraftEntry[];
  savedEntries: StrategyDraftEntry[];
  memberCount: number;
}) {
  const [step, setStep] = useState<"review" | "confirm">("review");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const rows = useMemo<ChangeRow[]>(() => {
    const diff = computeDraftDiff(entries, savedEntries);
    const result: ChangeRow[] = [];
    for (const d of diff.added) {
      result.push({ type: "Added", displayName: d.displayName, entityType: d.entityType, currentWeight: null, newWeight: d.toWeight ?? 0 });
    }
    for (const d of diff.removed) {
      result.push({ type: "Removed", displayName: d.displayName, entityType: d.entityType, currentWeight: d.fromWeight ?? 0, newWeight: null });
    }
    for (const d of diff.changed) {
      result.push({ type: "Modified", displayName: d.displayName, entityType: d.entityType, currentWeight: d.fromWeight ?? 0, newWeight: d.toWeight ?? 0 });
    }
    const savedMap = new Map<string, StrategyDraftEntry>();
    for (const s of savedEntries) savedMap.set(`${s.entityType}-${s.productId}`, s);
    for (const e of entries) {
      const saved = savedMap.get(`${e.entityType}-${e.productId}`);
      if (saved && saved.weight === e.weight) {
        result.push({ type: "Unchanged", displayName: e.displayName, entityType: e.entityType, currentWeight: e.weight, newWeight: e.weight });
      }
    }
    return result;
  }, [entries, savedEntries]);

  const handleClose = () => {
    if (isSaving) return;
    setStep("review");
    setSaveError(null);
    onClose();
  };

  const handleFinalConfirm = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onConfirm();
      setStep("review");
    } catch {
      setSaveError("Failed to save strategy. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-rp-separator">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {step === "review" ? "Review Strategy Changes" : "Confirm & Sync"}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-40"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === "review" && (
          <>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {rows.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No changes to save.</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-rp-separator">
                      <th className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left py-2.5 pr-3">Change</th>
                      <th className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left py-2.5 pr-3">Product</th>
                      <th className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left py-2.5 pr-3">Type</th>
                      <th className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right py-2.5 pr-3">Current</th>
                      <th className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right py-2.5">New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className="border-b border-gray-100 dark:border-rp-separator last:border-b-0">
                        <td className={`py-2.5 pr-3 text-sm font-medium ${TYPE_COLORS[row.type]}`}>{row.type}</td>
                        <td className="py-2.5 pr-3 text-sm text-gray-900 dark:text-white">{row.displayName}</td>
                        <td className="py-2.5 pr-3 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {row.entityType === "BASKET" ? "Basket" : "Portfolio"}
                        </td>
                        <td className="py-2.5 pr-3 text-sm font-mono text-right text-gray-900 dark:text-white">
                          {row.currentWeight !== null ? `${formatWeight(row.currentWeight)}%` : "-"}
                        </td>
                        <td className={`py-2.5 text-sm font-mono text-right ${
                          row.newWeight === null ? "text-gray-900 dark:text-white"
                            : row.currentWeight !== null && row.newWeight > row.currentWeight ? "text-green-500"
                            : row.currentWeight !== null && row.newWeight < row.currentWeight ? "text-red-400"
                            : row.type === "Added" ? "text-green-500"
                            : "text-gray-900 dark:text-white"
                        }`}>
                          {row.newWeight !== null ? `${formatWeight(row.newWeight)}%` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-rp-separator">
              <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                Cancel
              </button>
              <button
                onClick={() => setStep("confirm")}
                disabled={rows.length === 0}
                className="px-6 py-2.5 bg-rp-amber text-white font-medium rounded-lg hover:bg-yellow-600 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Accept Changes & Sync
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="px-6 py-8 text-center">
              <p className="text-base text-gray-900 dark:text-white">
                This will save the strategy template and sync it to{" "}
                <span className="font-semibold">{memberCount} member{memberCount !== 1 ? "s" : ""}</span>.
                Are you sure?
              </p>
              {saveError && (
                <p className="mt-4 text-sm text-red-500 dark:text-red-400">{saveError}</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-rp-separator">
              <button
                onClick={() => setStep("review")}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40"
              >
                Back
              </button>
              <button
                onClick={handleFinalConfirm}
                disabled={isSaving}
                className="px-6 py-2.5 bg-rp-amber text-white font-medium rounded-lg hover:bg-yellow-600 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSaving ? "Saving..." : "Save & Sync"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// -- Main editor component --

const GroupStrategyEditor: React.FC<GroupStrategyEditorProps> = ({
  strategy,
  productItems,
  productLookup,
  memberCount,
  onSave,
  onCancel,
}) => {
  const [editEntries, setEditEntries] = useState<EditEntry[]>(() =>
    (strategy ?? []).map((pw) => ({
      entityType: pw.entityType,
      productId: pw.productId,
      weight: pw.weight,
      displayName: resolveDisplayName(pw.entityType, pw.productId, productLookup),
    }))
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const savedDraftEntries = useMemo(
    () => toSavedDraftEntries(strategy, productLookup),
    [strategy, productLookup]
  );

  const totalWeight = useMemo(
    () => editEntries.reduce((sum, e) => sum + e.weight, 0),
    [editEntries]
  );

  const hasChanges = useMemo(() => {
    const saved = strategy ?? [];
    if (editEntries.length !== saved.length) return true;
    return editEntries.some((e) => {
      const orig = saved.find(
        (s) => s.entityType === e.entityType && s.productId === e.productId
      );
      if (!orig) return true;
      return Math.abs(e.weight - orig.weight) > 0.001;
    });
  }, [editEntries, strategy]);

  const isOverAllocationLimit = totalWeight > BASE_STRATEGY_ALLOCATION_LIMIT;
  const isSaveDisabled = isOverAllocationLimit || !hasChanges;

  const entryKeys = useMemo(
    () => new Set(editEntries.map((e) => `${e.entityType}-${e.productId}`)),
    [editEntries]
  );

  const nonZeroDraftEntries = useMemo(() => toDraftEntries(editEntries), [editEntries]);
  const diff = useMemo(
    () => computeDraftDiff(nonZeroDraftEntries, savedDraftEntries),
    [nonZeroDraftEntries, savedDraftEntries]
  );
  const unchangedEntries = useMemo(() => {
    const savedMap = new Map<string, StrategyDraftEntry>();
    for (const s of savedDraftEntries) savedMap.set(`${s.entityType}-${s.productId}`, s);
    return editEntries.filter((e) => {
      const saved = savedMap.get(`${e.entityType}-${e.productId}`);
      return saved && saved.weight === e.weight && e.weight > 0;
    });
  }, [editEntries, savedDraftEntries]);

  const hasDiffSections = diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0;

  const entryMap = useMemo(() => {
    const map = new Map<string, EditEntry>();
    for (const e of editEntries) map.set(`${e.entityType}-${e.productId}`, e);
    return map;
  }, [editEntries]);

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return productItems
      .filter((p) => {
        const key = `${p.productType}-${p.productId}`;
        if (entryKeys.has(key)) return false;
        return (
          p.title.toLowerCase().includes(q) ||
          p.providerName.toLowerCase().includes(q) ||
          String(p.productId).includes(q)
        );
      })
      .slice(0, 20);
  }, [searchQuery, productItems, entryKeys]);

  const handleWeightChange = useCallback(
    (entityType: string, productId: number, raw: string) => {
      const parsed = parseFloat(raw);
      const val = isNaN(parsed) || parsed < 0 ? 0 : parsed;
      setEditEntries((prev) =>
        prev.map((e) =>
          e.entityType === entityType && e.productId === productId
            ? { ...e, weight: val }
            : e
        )
      );
    },
    []
  );

  const handleRemove = useCallback(
    (entityType: string, productId: number) => {
      setEditEntries((prev) =>
        prev.filter(
          (e) => !(e.entityType === entityType && e.productId === productId)
        )
      );
    },
    []
  );

  const handleUndoRemoved = useCallback(
    (d: DraftDiffItem) => {
      setEditEntries((prev) => [
        ...prev,
        {
          entityType: d.entityType,
          productId: d.productId,
          weight: d.fromWeight ?? 0,
          displayName: d.displayName,
        },
      ]);
    },
    []
  );

  const handleUndoModified = useCallback(
    (d: DraftDiffItem) => {
      if (d.fromWeight === undefined) return;
      setEditEntries((prev) =>
        prev.map((e) =>
          e.entityType === d.entityType && e.productId === d.productId
            ? { ...e, weight: d.fromWeight! }
            : e
        )
      );
    },
    []
  );

  const handleAddProduct = useCallback(
    (item: ProductSearchItem) => {
      const key = `${item.productType}-${item.productId}`;
      if (entryKeys.has(key)) return;
      setEditEntries((prev) => [
        ...prev,
        {
          entityType: item.productType,
          productId: item.productId,
          weight: 0,
          displayName: `[${item.providerName}] ${item.title}`,
        },
      ]);
      setSearchQuery("");
      setShowDropdown(false);
    },
    [entryKeys]
  );

  const handleSave = useCallback(async () => {
    const payload: ProductWeight[] = editEntries
      .filter((e) => e.weight > 0)
      .map((e) => ({
        entityType: e.entityType,
        productId: e.productId,
        weight: e.weight,
        position: "LONG" as const,
      }));
    // Let errors propagate so the confirmation modal surfaces the failure.
    await onSave(payload);
    setShowConfirmModal(false);
  }, [editEntries, onSave]);

  const preventScrollChange = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  const inputBaseClass =
    "bg-transparent dark:bg-rp-surface-raised border rounded py-1 text-sm font-mono text-right text-gray-900 dark:text-white appearance-none focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber";

  const COL_COUNT = 5;

  const renderSectionHeader = (label: string, colorClass: string, icon?: React.ReactNode) => (
    <tr key={`section-${label}`}>
      <td colSpan={COL_COUNT} className="px-4 pt-4 pb-2 border-t-2 border-gray-300 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {icon}
          <span className={`text-xs font-semibold uppercase tracking-wider ${colorClass}`}>{label}</span>
        </div>
      </td>
    </tr>
  );

  const renderEntryRow = (entry: EditEntry) => (
    <tr
      key={`${entry.entityType}-${entry.productId}`}
      className="border-b border-gray-100 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors"
    >
      <td className="px-4 py-2.5">
        <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
          {entry.entityType}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-400 dark:text-gray-500">
        {entry.productId}
      </td>
      <td className="px-4 py-2.5">
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
          {entry.displayName}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="relative inline-block w-20">
          <input
            type="number"
            min={0}
            max={BASE_STRATEGY_ALLOCATION_LIMIT}
            step={1}
            aria-label={`${entry.displayName} weight`}
            value={entry.weight ? roundToDecimals(entry.weight) : ""}
            placeholder="0"
            onWheel={preventScrollChange}
            onChange={(e) => handleWeightChange(entry.entityType, entry.productId, e.target.value)}
            className={`w-full pl-2 pr-6 ${inputBaseClass} ${
              entry.weight === 0 ? "border-rp-amber/50" : "border-gray-200 dark:border-rp-separator"
            }`}
          />
          <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-500 dark:text-gray-400">%</span>
        </div>
      </td>
      <td className="px-4 py-2.5 text-center">
        <button
          aria-label={`Remove ${entry.displayName}`}
          onClick={() => handleRemove(entry.entityType, entry.productId)}
          className="text-gray-400 hover:text-red-400 transition-colors p-1 rounded hover:bg-red-600/10"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );

  const renderModifiedRow = (entry: EditEntry, d: DraftDiffItem) => (
    <tr
      key={`${entry.entityType}-${entry.productId}`}
      className="border-b border-gray-100 dark:border-rp-separator last:border-b-0 hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-colors"
    >
      <td className="px-4 py-2.5">
        <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
          {entry.entityType}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-400 dark:text-gray-500">
        {entry.productId}
      </td>
      <td className="px-4 py-2.5">
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
          {entry.displayName}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="relative inline-block w-20">
          <input
            type="number"
            min={0}
            max={BASE_STRATEGY_ALLOCATION_LIMIT}
            step={1}
            aria-label={`${entry.displayName} weight`}
            value={entry.weight ? roundToDecimals(entry.weight) : ""}
            placeholder="0"
            onWheel={preventScrollChange}
            onChange={(e) => handleWeightChange(entry.entityType, entry.productId, e.target.value)}
            className={`w-full pl-2 pr-6 ${inputBaseClass} ${
              entry.weight === 0 ? "border-rp-amber/50" : "border-gray-200 dark:border-rp-separator"
            }`}
          />
          <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-500 dark:text-gray-400">%</span>
        </div>
      </td>
      <td className="px-4 py-2.5 text-center">
        <button
          onClick={() => handleUndoModified(d)}
          className="text-xs font-medium text-rp-amber border border-rp-amber px-2 py-1 rounded transition-colors hover:bg-rp-amber/10"
        >
          Undo
        </button>
      </td>
    </tr>
  );

  const renderRemovedRow = (d: DraftDiffItem) => (
    <tr
      key={`removed-${d.entityType}-${d.productId}`}
      className="border-b border-gray-100 dark:border-rp-separator last:border-b-0 opacity-60"
    >
      <td className="px-4 py-2.5">
        <span className="px-2 py-0.5 bg-gray-100 dark:bg-rp-surface-raised text-gray-600 dark:text-gray-400 text-[10px] font-semibold uppercase tracking-wider rounded border border-gray-200 dark:border-gray-800">
          {d.entityType}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-xs text-gray-400 dark:text-gray-500">
        {d.productId}
      </td>
      <td className="px-4 py-2.5">
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate block line-through">
          {d.displayName}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        <span className="text-sm font-mono text-gray-400 line-through">{formatWeight(d.fromWeight ?? 0)}%</span>
      </td>
      <td className="px-4 py-2.5 text-center">
        <button
          onClick={() => handleUndoRemoved(d)}
          className="text-xs font-medium text-rp-amber border border-rp-amber px-2 py-1 rounded transition-colors hover:bg-rp-amber/10"
        >
          Undo
        </button>
      </td>
    </tr>
  );

  const buildTableRows = () => {
    if (!hasDiffSections) {
      return editEntries.map((entry) => renderEntryRow(entry));
    }

    const rows: React.ReactNode[] = [];

    if (diff.added.length > 0) {
      rows.push(renderSectionHeader(`+${diff.added.length} Added`, "text-green-500", <Plus className="w-3.5 h-3.5 text-green-500" />));
      for (const d of diff.added) {
        const entry = entryMap.get(`${d.entityType}-${d.productId}`);
        if (entry) rows.push(renderEntryRow(entry));
      }
    }
    if (diff.removed.length > 0) {
      rows.push(renderSectionHeader(`-${diff.removed.length} Removed`, "text-red-400", <Minus className="w-3.5 h-3.5 text-red-400" />));
      for (const d of diff.removed) rows.push(renderRemovedRow(d));
    }
    if (diff.changed.length > 0) {
      rows.push(renderSectionHeader(`~${diff.changed.length} Modified`, "text-rp-amber", <RefreshCw className="w-3.5 h-3.5 text-rp-amber" />));
      for (const d of diff.changed) {
        const entry = entryMap.get(`${d.entityType}-${d.productId}`);
        if (entry) rows.push(renderModifiedRow(entry, d));
      }
    }
    if (unchangedEntries.length > 0) {
      rows.push(renderSectionHeader(`${unchangedEntries.length} Unchanged`, "text-gray-500"));
      for (const entry of unchangedEntries) rows.push(renderEntryRow(entry));
    }

    const zeroWeightNew = editEntries.filter((e) => {
      if (e.weight > 0) return false;
      const key = `${e.entityType}-${e.productId}`;
      return !savedDraftEntries.some((s) => `${s.entityType}-${s.productId}` === key);
    });
    if (zeroWeightNew.length > 0) {
      rows.push(renderSectionHeader(`${zeroWeightNew.length} Pending weight`, "text-gray-400"));
      for (const entry of zeroWeightNew) rows.push(renderEntryRow(entry));
    }

    return rows;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
      {/* Editor table */}
      <div className="bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Edit Strategy Template
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={isSaveDisabled}
              onClick={() => setShowConfirmModal(true)}
              className="px-4 py-1.5 text-sm font-medium bg-rp-amber text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              Review & Save
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-rp-separator">
                <th className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-left px-4 py-2.5 w-20">Type</th>
                <th className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-right px-4 py-2.5 w-16">ID</th>
                <th className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-left px-4 py-2.5">Product</th>
                <th className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 text-right px-4 py-2.5 w-28">Weight (%)</th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {editEntries.length === 0 && diff.removed.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                    No products in strategy. Add products below.
                  </td>
                </tr>
              ) : (
                buildTableRows()
              )}
            </tbody>
          </table>
        </div>

        {/* Product search */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-rp-separator relative">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search products to add..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(e.target.value.trim().length > 0);
              }}
              onFocus={() => {
                if (searchQuery.trim()) setShowDropdown(true);
              }}
              className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(""); setShowDropdown(false); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {showDropdown && filteredProducts.length > 0 && (
            <div className="absolute left-0 right-0 bottom-full mb-1 mx-4 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg max-h-60 overflow-y-auto z-20">
              {filteredProducts.map((item) => (
                <button
                  key={`${item.productType}-${item.productId}`}
                  onClick={() => handleAddProduct(item)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors flex items-center justify-between"
                >
                  <span className="text-gray-900 dark:text-white truncate">[{item.providerName}] {item.title}</span>
                  <span className="text-xs font-mono text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">{item.productType} #{item.productId}</span>
                </button>
              ))}
            </div>
          )}
          {showDropdown && searchQuery.trim() && filteredProducts.length === 0 && (
            <div className="absolute left-0 right-0 bottom-full mb-1 mx-4 bg-white dark:bg-rp-surface-raised border border-gray-200 dark:border-rp-separator rounded-lg shadow-lg z-20">
              <div className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">No matching products found</div>
            </div>
          )}
        </div>

        {/* Total + validation */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-rp-separator flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className={`text-sm font-mono font-medium ${
              !isOverAllocationLimit ? "text-green-600 dark:text-green-500" : "text-red-600 dark:text-red-500"
            }`}>
              Total: {formatWeight(totalWeight)}% / {formatWeight(BASE_STRATEGY_ALLOCATION_LIMIT)}%
            </span>
            {isOverAllocationLimit && (
              <span className="text-red-600 dark:text-red-500 text-xs">
                Exceeds {formatWeight(BASE_STRATEGY_ALLOCATION_LIMIT)}% -- reduce weights to save
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Donut sidebar */}
      <div className="hidden lg:block">
        <AllocationDonut entries={editEntries} totalWeight={totalWeight} />
      </div>

      {/* Save confirmation modal */}
      <GroupStrategySaveConfirmation
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSave}
        entries={nonZeroDraftEntries}
        savedEntries={savedDraftEntries}
        memberCount={memberCount}
      />
    </div>
  );
};

export default GroupStrategyEditor;
